import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApolloClient, useMutation } from '@apollo/client';
import { Building2, Heart } from 'lucide-react';
import { TOGGLE_FAVORITE_BRAND } from '@/graphql/mutations';
import { entityPageRequests } from '@/graphql/entityRoutes';
import { useAuth } from '@/context/AuthContext';
import { trackClick } from '@/services/tracking';
import { trackFavorite } from '@/lib/analytics';
import { useHoverPrefetch } from '@/hooks/useHoverPrefetch';
import { Img } from '@/components/ui';
import { CARD_LOGO_SIZES, THUMB_SRCSET_WIDTHS } from '@/utils/safeImageUrl';
import { displayName } from '@/seo/text';
import { brandLogoAlt } from '@/seo/templates';
import { entityPath } from '@/seo/urls';
import type { Brand, BrandMinimal } from '@/types';

interface BrandCardProps {
  brand: Brand | BrandMinimal;
  compact?: boolean;
  /** The logo is likely the page's LCP element (first cards of a list): eager and visible at once (audit F6). */
  priority?: boolean;
}

export function BrandCard({ brand, compact = false, priority = false }: BrandCardProps) {
  const navigate = useNavigate();
  const client = useApolloClient();
  const { token } = useAuth();
  const [favorite, setFavorite] = useState(!!brand.isFavorite);
  const [toggleFavorite] = useMutation(TOGGLE_FAVORITE_BRAND);

  useEffect(() => {
    setFavorite(!!brand.isFavorite);
  }, [brand.isFavorite]);

  // null when the query did not request `promotions` (the store page's brand
  // fragment): the stat is hidden then, never shown as a false "0".
  const promotionCount = brand.promotions ? brand.promotions.length : null;
  // Shown and announced cleaned (𝗯𝗼𝗹𝗱 letters, emoji, hashtags out), like the brand page's H1.
  const name = displayName(brand.name);
  // "{name} logo" (plan alt formulas); the name text beside it is not the image's description.
  const logoAlt = brandLogoAlt(brand.name);

  const handleClick = () => {
    trackClick('brand', brand.id);
  };

  // The brand's canonical path (its `urlPath` when the data has it, else the
  // id path; web plan W0.16). Warm the Apollo cache with the queries that page
  // runs once the pointer rests on the card (or it takes keyboard focus) so
  // the first tap into it paints instantly. At most once per card instance;
  // see useHoverPrefetch.
  const to = entityPath(brand, 'brand');
  const prefetchDetail = useHoverPrefetch(entityPageRequests('brand', brand));

  const handleHeart = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!token) {
      navigate('/login');
      return;
    }
    const next = !favorite;
    const cacheId = client.cache.identify({
      __typename: (brand as { __typename?: string }).__typename ?? 'Brand',
      id: brand.id,
    });
    setFavorite(next);
    toggleFavorite({
      variables: { id: brand.id },
      update: (cache) => {
        cache.modify({ id: cacheId, fields: { isFavorite: () => next } });
      },
    })
      .then(() => {
        if (next) trackFavorite({ contentType: 'brand', itemId: brand.id });
      })
      .catch(() => {
        setFavorite(!next);
        client.cache.modify({ id: cacheId, fields: { isFavorite: () => !next } });
      });
  };

  if (compact) {
    return (
      <Link
        to={to}
        onClick={handleClick}
        {...prefetchDetail}
        className="group flex w-24 shrink-0 flex-col items-center gap-2"
      >
        <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-2xl border border-line bg-surface p-3 shadow-sm transition-all duration-200 group-hover:ring-2 group-hover:ring-primary/40">
          <Img
            media={brand.logo}
            transform={{ width: 70, fit: 'contain' }}
            sizes="70px"
            widths={THUMB_SRCSET_WIDTHS}
            alt={logoAlt}
            className="h-full w-full object-contain"
            fallback={<Building2 size={32} className="text-ink-tertiary" />}
          />
        </div>
        <span className="w-full text-center text-xs font-medium text-ink line-clamp-1">
          {name}
        </span>
      </Link>
    );
  }

  // The heart is a sibling of the link, not inside it: interactive content
  // nested in an <a> is invalid HTML and reads as one muddled link to screen
  // readers. The wrapper carries the hover lift so both move together.
  return (
    <div className="group relative transition-transform duration-200 hover:-translate-y-0.5">
      <Link
        to={to}
        onClick={handleClick}
        {...prefetchDetail}
        className="block h-full overflow-hidden rounded-2xl border border-card-border bg-card transition-shadow duration-200 group-hover:shadow-lg"
      >
        <div className="flex h-32 items-center justify-center bg-surface p-4">
          <Img
            media={brand.logo}
            transform={{ width: 300, fit: 'contain' }}
            sizes={CARD_LOGO_SIZES}
            alt={logoAlt}
            priority={priority}
            className="h-full w-full object-contain"
            fallback={<Building2 size={40} className="text-ink-tertiary" />}
          />
        </div>
        <div className="border-t border-line-light p-3">
          <p className="text-sm font-semibold text-primary-strong line-clamp-1">{name}</p>
          {promotionCount !== null && (
            <p className="mt-0.5 text-xs text-ink-secondary">
              {promotionCount} {promotionCount === 1 ? 'Promotion' : 'Promotions'}
            </p>
          )}
        </div>
      </Link>
      {/* Hidden until hover on desktop, but also revealed while the card's link
          has keyboard focus so a keyboard user can see what Tab reaches next. */}
      <button
        type="button"
        onClick={handleHeart}
        aria-label={
          favorite ? `Remove ${name} from favorites` : `Add ${name} to favorites`
        }
        className={`absolute right-2 top-2 z-10 rounded-full bg-surface/90 p-1.5 shadow backdrop-blur transition-all duration-200 focus-visible:opacity-100 ${
          favorite
            ? 'opacity-100'
            : 'opacity-100 md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100'
        }`}
      >
        <Heart
          size={18}
          fill={favorite ? 'currentColor' : 'none'}
          className={favorite ? 'text-error' : 'text-ink-tertiary'}
          aria-hidden="true"
        />
      </button>
    </div>
  );
}

export default BrandCard;
